import React from 'react';
import {
  calculateIncreaseAmount,
  calculateIncreasePercentage,
} from '../utils/salaryDataProcessor';
import styles from '../SalaryCalculator.module.css';
import { Occupation } from '../types';
import numberFormatter from '@/utils/numberFormatter';
import TrendingUp from '@/components/DesignSystem/Icons/svgs/TrendingUp';

interface SalaryIncreasePanelProps {
  inputtedSalary: number | null;
  selectedOccupationCard: Occupation | null;
}

const SalaryIncreasePanel: React.FC<SalaryIncreasePanelProps> = ({
  inputtedSalary,
  selectedOccupationCard,
}) => {
  const { currencyFormat } = numberFormatter();
  const increasePercentage =
    inputtedSalary && selectedOccupationCard?.salary
      ? calculateIncreasePercentage(
          inputtedSalary,
          selectedOccupationCard?.salary,
        )
      : 0;

  const increaseAmount =
    inputtedSalary && selectedOccupationCard?.salary
      ? calculateIncreaseAmount(inputtedSalary, selectedOccupationCard?.salary)
      : 0;

  // Determine if salary is missing or no occupation card is selected
  const isSalaryMissing = !inputtedSalary || inputtedSalary <= 0;
  const isOccupationMissing = !selectedOccupationCard;
  const hasError = isSalaryMissing || isOccupationMissing;

  return (
    <div
      className={`rounded-lg p-6 my-4 md:my-6 ${hasError ? 'bg-[#fef2f2] border border-[#fecaca]' : 'bg-primary-25'}`}
    >
      <div className='flex justify-between'>
        <div>
          <div className='flex items-center mb-4'>
            <div
              className={`p-1 rounded-full mr-2 ${hasError ? 'bg-[#fee2e2] text-[#ef4444]' : 'bg-primary-50'}`}
            >
              <TrendingUp />
            </div>
            <div className='font-bold font-body-14 text-body'>
              Potential Salary Increase
            </div>
          </div>

          <div
            className={`font-body-24 font-bold mb-4 ${hasError ? 'text-[#ef4444]' : 'text-primary-500'}`}
          >
            {hasError ? '--' : `${increasePercentage.toFixed(1)}%`}
          </div>

          <p className='text-gray-700 font-body-14'>
            {inputtedSalary && selectedOccupationCard?.salary ? (
              <>
                With an occupation in {selectedOccupationCard?.title}, you could
                earn approximately {currencyFormat?.format(increaseAmount)} more
                per year{' '}
              </>
            ) : (
              <>
                <span
                  className={`font-medium ${hasError ? 'text-[#dc2626]' : ''}`}
                >
                  To see your potential salary increase:
                </span>{' '}
                {isOccupationMissing && isSalaryMissing
                  ? 'Click on a salary card above and enter your current salary in the input field.'
                  : isOccupationMissing
                    ? 'Click on a salary card above to select an occupation.'
                    : 'Enter your current salary in the input field above.'}
              </>
            )}
          </p>
        </div>

        <div className='flex items-center'>
          <div className='relative w-24 h-24'>
            <div className='w-24 h-24 rounded-full flex items-center justify-center'>
              <div
                className={`text-2xl font-bold ${hasError ? 'text-[#ef4444]' : 'text-primary-500'}`}
              >
                {hasError ? '--' : `${Math.round(increasePercentage)}%`}
              </div>
            </div>

            {/* Background track */}
            <svg
              className='absolute top-0 left-0 w-24 h-24'
              viewBox='0 0 100 100'
            >
              <circle
                cx='50'
                cy='50'
                r='40'
                fill='none'
                className={hasError ? 'text-[#fee2e2]' : 'text-primary-100'}
                stroke='currentColor'
                strokeWidth='5'
              />
            </svg>

            {/* Animated progress with gradient - only show if not missing salary */}
            {!hasError && (
              <svg
                className='absolute top-0 left-0 w-24 h-24'
                viewBox='0 0 100 100'
              >
                <circle
                  cx='50'
                  cy='50'
                  r='40'
                  fill='none'
                  className={`${styles.textGradientToR} from-primary-500 to-secondary-500 transition-all duration-1000 ease-out`}
                  stroke='url(#gradient)'
                  strokeWidth='5'
                  strokeDasharray='252'
                  strokeDashoffset={
                    252 - Math.min(increasePercentage * 2.52, 300)
                  }
                  strokeLinecap='round'
                  transform='rotate(-90 50 50)'
                />
                <defs>
                  <linearGradient
                    id='gradient'
                    x1='0%'
                    y1='0%'
                    x2='100%'
                    y2='0%'
                  >
                    <stop
                      offset='0%'
                      className='text-primary-500'
                      stopColor='currentColor'
                    />
                    <stop
                      offset='100%'
                      className='text-secondary-500'
                      stopColor='currentColor'
                    />
                  </linearGradient>
                </defs>
              </svg>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryIncreasePanel;
